/**
 * Signin Component
 * Handles user authentication including email/password login and social authentication
 * Provides form validation, error handling, and navigation to other auth flows
 */

// React core imports
import React, { useEffect, useState } from "react";

// NextUI components for UI elements
import { Button, Divider, Image, Input, Link } from "@nextui-org/react";

// Custom icon components
import { EyeSlashFilledIcon } from "../../Images/EyeSlashFilledIcon";
import { EyeFilledIcon } from "../../Images/EyeFilledIcon";
import Google from "../../Images/Google.jsx";

// API controllers for data fetching and authentication
import { getAlldata, getAllRoles } from "../../controllers/APIController.js";
import { NavigateSign } from "../../controllers/loginController.js";

// React Router hooks for navigation
import { useNavigate, useLocation } from "react-router-dom";

// Custom components
import Loading from "../../components/Loader/Loading.jsx";

// Social authentication controllers
import { GitHubAuth, GoogleAuth } from "../../controllers/social.js";

// Utility functions
import { decryptedText } from "../../utils/helper.js";

/**
 * Main Signin Component
 * Manages user authentication flow with comprehensive validation
 * 
 * @returns {JSX.Element} Signin form component
 */
const Signin = () => {
  // Navigation hooks
  const navigate = useNavigate();
  const location = useLocation();

  // State management for form inputs and validation
  const [isVisible, setIsVisible] = useState(false);
  const [users, setUsers] = useState(null);
  const [validationConditions, setValidationConditions] = useState(false);
  const [decryptedPassword, setDecryptedPassword] = useState("");
  const [loader, setLoader] = useState(false);
  
  // Form data state
  const [loginDetails, setLoginDetails] = useState({
    email: "",
    password: "",
  });

  // Error state management
  const [loginError, setLoginError] = useState({
    emailError: "",
    passwordError: "",
    finalError: "",
  });

  // Regular expressions for validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  /**
   * Handles Google OAuth authentication
   * Redirects user to Google authentication flow
   * 
   * @param {Event} e - Click event
   */
  const handleGoogleAuth = async (e) => {
    e.stopPropagation();
    try {
      const data = await GoogleAuth();
      window.location.href = data.urlWithQueryParams;
    } catch (error) {
      console.error("Google authentication error:", error);
    }
  };

  /**
   * Handles passwordless authentication flow
   * Navigates user to passwordless sign-in page
   * 
   * @param {Event} e - Click event
   */
  const handlePasswordlessAuth = async (e) => {
    e.stopPropagation();
    navigate("/passwordless-sign");
  };

  /**
   * Navigates to password reset page
   */
  const navigateForgot = () => {
    navigate("/auth/reset-password");
  };

  /**
   * Toggles password visibility state
   */
  const toggleVisibility = () => setIsVisible(!isVisible);

  // Initialize component data on mount
  useEffect(() => {
    // Fetch all users for validation
    getAlldata("users")
      .then((data) => setUsers(data))
      .catch((error) => console.error("Error fetching users:", error));

    // Clear user data if accessing sign-in page directly
    if (location.pathname === "/sign-in") {
      sessionStorage.removeItem("userData");
    }

    // Fetch user roles for authorization
    getAllRoles();
  }, [location.pathname]);

  // Check if email exists in user database
  const emailExists = users && users?.filter(
    (data) => data?.email === loginDetails?.email?.toLowerCase()?.trim()
  );

  /**
   * Decrypts stored password for comparison
   * Attempts to decrypt the password hash for validation
   */
  const handleDecrypt = () => {
    try {
      const id = emailExists && emailExists.length > 0 && emailExists[0].Password2;
      
      // Validate that we have a valid encrypted password before attempting decryption
      if (!id || typeof id !== 'string' || id.trim() === '') {
        console.log("No valid encrypted password found for user");
        setDecryptedPassword(null);
        return;
      }
      
      const decrypted = decryptedText(id);
      setDecryptedPassword(decrypted);
    } catch (error) {
      console.error("Password decryption failed:", error);
      setDecryptedPassword(null);
    }
  };

  // Decrypt password when email exists
  useEffect(() => {
    // Only attempt decryption if we have an email and emailExists data
    if (loginDetails.email && emailExists && emailExists.length > 0) {
      handleDecrypt();
    } else {
      // Reset decrypted password if no email or no matching user
      setDecryptedPassword(null);
    }
  }, [emailExists, loginDetails.email]);

  // Check if entered password matches decrypted password
  const comparedPassword = emailExists && 
    emailExists.length > 0 && 
    decryptedPassword !== null && 
    JSON.stringify(decryptedPassword) !== JSON.stringify(loginDetails.password);

  /**
   * Validates form inputs and sets appropriate error messages
   * Checks email format, password requirements, and credential validity
   * 
   * @returns {boolean} True if validation passes, false otherwise
   */
  const validation = () => {
    let isValid = true;

    // Email validation
    if (loginDetails.email === "") {
      isValid = false;
      setLoginError((previous) => ({
        ...previous,
        emailError: "Email is required.",
      }));
    } else if (!emailRegex.test(loginDetails.email)) {
      isValid = false;
      setLoginError((previous) => ({
        ...previous,
        emailError: "Please enter a valid email address.",
      }));
    } else {
      setLoginError((previous) => ({
        ...previous,
        emailError: "",
      }));
    }

    // Password validation
    if (loginDetails.password === "") {
      isValid = false;
      setLoginError((previous) => ({
        ...previous,
        passwordError: "Password is required.",
      }));
    } else if (!passwordRegex.test(loginDetails.password)) {
      isValid = false;
      setLoginError((previous) => ({
        ...previous,
        passwordError: "Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.",
      }));
    } else {
      setLoginError((previous) => ({
        ...previous,
        passwordError: "",
      }));
    }

    // Credential validation
    if ((emailExists && emailExists?.length === 0) || comparedPassword) {
      isValid = false;
      setLoginError((previous) => ({
        ...previous,
        passwordError: "The email and password combination you entered does not match our records.",
        finalError: null
      }));
    }
    
    return isValid;
  };

  /**
   * Handles Enter key press to submit form
   * Allows users to submit by pressing Enter key
   * 
   * @param {KeyboardEvent} event - Key press event
   */
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      submitHandler();
    }
  };

  /**
   * Handles form submission and authentication
   * Validates inputs, attempts login, and navigates on success
   */
  const submitHandler = async () => {
    setLoader(true);
    const isValid = validation();
    
    if (isValid) {
      try {
        setValidationConditions(false);
        
        // Attempt to authenticate user
        const navigateRoute = await NavigateSign(
          loginDetails.email,
          loginDetails.password
        );
        
        if (navigateRoute && navigateRoute !== "") {
          setLoader(false);
          navigate(navigateRoute);
        } else {
          setLoader(false);
        }
      } catch (error) {
        console.error("Authentication error:", error);
        setLoader(false);
      }
    } else {
      setValidationConditions(true);
      setLoader(false);
    }
  };

  return (
    <>
      {loader ? (
        <div
          className={`absolute z-[100]  inset-0 h-screen w-screen  bg-neutral-50'`}
        >
          <Loading />
        </div>
      ) : (
        ""
      )}

      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 text-sm">
        {/* Content */}
        <div className="w-full max-w-md">
            {/* Form Container */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-6">
                        <Image width={48} alt="Audyt Logo" src="Assets/logo.svg" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Welcome back
                    </h1>
                    <p className="text-gray-600 text-sm">
                        Sign in to your account
                    </p>
                </div>

                {/* Error Message */}
                {loginError.finalError && (
                    <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-error-700 text-sm">
                            {loginError.finalError}
                        </p>
                    </div>
                )}

                {/* Main Sign In Form */}
                <form data-gtm-form-interact-id="0" onKeyDown={handleKeyPress} className="space-y-6 mb-6">
                    {/* Email Input */}
                    <div>
                        <Input
                            radius="md"
                            variant="bordered"
                            isInvalid={loginError?.emailError && loginError?.emailError !== ""}
                            errorMessage={loginError?.emailError && loginError?.emailError}
                            type="email"
                            label="Email"
                            placeholder="Enter your email"
                            onChange={(e) =>
                              setLoginDetails({ ...loginDetails, email: e.target.value })
                            }
                            labelPlacement="outside"
                            style={{ width: '100%', maxWidth: '100%' }}
                            classNames={{
                                label: "text-gray-700 font-medium text-sm",
                                input: "text-gray-900 text-sm",
                                inputWrapper: "bg-white border-gray-300 hover:border-primary-400 focus-within:border-primary-500 transition-all duration-200 h-10 w-full max-w-full",
                            }}
                        />
                    </div>

                    {/* Password Input */}
                    <div>
                        <Input
                            radius="md"
                            variant="bordered"
                            onChange={(e) =>
                              setLoginDetails({ ...loginDetails, password: e.target.value })
                            }
                            isInvalid={loginError?.passwordError && loginError?.passwordError !== ""}
                            errorMessage={loginError?.passwordError}
                            label="Password"
                            labelPlacement="outside"
                            placeholder="Enter your password"
                            style={{ width: '100%', maxWidth: '100%' }}
                            endContent={
                                <button
                                    className="focus:outline-none"
                                    type="button"
                                    onClick={toggleVisibility}
                                >
                                    {isVisible ? (
                                        <EyeSlashFilledIcon className="text-lg text-gray-400 hover:text-gray-600 transition-colors" />
                                    ) : (
                                        <EyeFilledIcon className="text-lg text-gray-400 hover:text-gray-600 transition-colors" />
                                    )}
                                </button>
                            }
                            type={isVisible ? "text" : "password"}
                            classNames={{
                                label: "text-gray-700 font-medium text-sm",
                                input: "text-gray-900 text-sm",
                                inputWrapper: "bg-white border-gray-300 hover:border-primary-400 focus-within:border-primary-500 transition-all duration-200 h-10 w-full max-w-full",
                            }}
                        />
                        <div className="flex justify-end mt-2">
                            <button
                                type="button"
                                onClick={navigateForgot}
                                className="text-primary-600 hover:text-primary-700 font-semibold text-sm transition-colors"
                            >
                                Forgot password?
                            </button>
                        </div>
                    </div>

                    {/* Sign In Button */}
                    <Button
                        id="CreateAccount"
                        radius="md"
                        onClick={submitHandler}
                        className="w-full h-10 bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-all duration-200 text-sm"
                        size="sm"
                    >
                        Sign In
                        <lord-icon
                            src="https://cdn.lordicon.com/vduvxizq.json"
                            trigger="hover"
                            target="#CreateAccount"
                            
                            style={{
                                width: "16px",
                                height: "16px",
                            }}
                        />
                    </Button>
                </form>

                {/* Divider */}
                <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-gray-500 font-medium">or</span>
                    </div>
                </div>

                {/* Alternative Sign In Options */}
                <div className="space-y-3 mb-6">
                    <Button
                        radius="md"
                        variant="bordered"
                        className="w-full h-10 bg-white border-gray-300 hover:border-gray-400 transition-all duration-200 text-sm font-medium text-gray-700"
                        onClick={handleGoogleAuth}
                    >
                        <Image width={16} alt="Google" src="Images/Google.webp" className="mr-3" />
                        Continue with Google
                    </Button>

                    <Button
                        radius="md"
                        variant="bordered"
                        className="w-full h-10 bg-white border-gray-300 hover:border-gray-400 transition-all duration-200 text-sm font-medium text-gray-700"
                        onClick={handlePasswordlessAuth}
                    >
                        <lord-icon
                            src="https://cdn.lordicon.com/yxczfiyc.json"
                            trigger="hover"
                            
                            style={{
                                width: "16px",
                                height: "16px",
                            }}
                        />
                        Sign in Passwordless
                    </Button>
                </div>

                {/* Create Account Link */}
                <div className="text-center mb-6">
                    <span className="text-gray-600 text-sm">
                        Don't have an account?{" "}
                        <button
                            onClick={() => navigate("/sign-up")}
                            className="text-primary-600 hover:text-primary-700 font-semibold transition-colors"
                        >
                            Create one
                        </button>
                    </span>
                </div>

                {/* Terms */}
                <div className="pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center leading-relaxed">
                        By signing in, you agree to our{" "}
                        <Link href="#" size="sm" className="text-primary-600 hover:text-primary-700">
                            Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link href="#" size="sm" className="text-primary-600 hover:text-primary-700">
                            Privacy Policy
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </div>
      </div>
    </>
  );
};

export default Signin;
